import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dice5, LogOut, Trophy, LayoutDashboard, Plus, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const active = (p) => location.pathname === p;

  return (
    <header className="sticky top-0 z-40 glass border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center shadow-[0_0_24px_-4px_rgba(245,158,11,0.6)]">
            <Dice5 className="w-5 h-5 text-slate-950" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-mono-data text-xs text-amber-400 tracking-widest">MEEPLE.MATCH</span>
            <span className="text-slate-100 font-semibold text-sm">Tournament OS</span>
          </div>
        </Link>
        {user ? (
          <nav className="flex items-center gap-1">
            <Link to="/dashboard" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active('/dashboard') ? 'bg-slate-800/70 text-amber-300' : 'text-slate-300 hover:text-white'}`} data-testid="nav-dashboard">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/history" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active('/history') ? 'bg-slate-800/70 text-amber-300' : 'text-slate-300 hover:text-white'}`} data-testid="nav-history">
              <Trophy className="w-4 h-4" /> History
            </Link>
            {user.role === "organizer" && user.organizer_status === "approved" && (
              <Link to="/create" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active('/create') ? 'bg-slate-800/70 text-amber-300' : 'text-slate-300 hover:text-white'}`} data-testid="nav-create">
                <Plus className="w-4 h-4" /> New
              </Link>
            )}
            {user.role === "super_admin" && (
              <>
                <Link to="/create" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active('/create') ? 'bg-slate-800/70 text-amber-300' : 'text-slate-300 hover:text-white'}`} data-testid="nav-create">
                  <Plus className="w-4 h-4" /> New
                </Link>
                <Link to="/admin" className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 ${active('/admin') ? 'bg-slate-800/70 text-amber-300' : 'text-slate-300 hover:text-white'}`} data-testid="nav-admin">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              </>
            )}
            <div className="mx-3 h-8 w-px bg-white/10" />
            <div className="flex items-center gap-3 pr-2">
              <div className="text-right hidden md:block">
                <div className="text-xs text-slate-500 uppercase tracking-wider flex items-center justify-end gap-1.5">
                  {user.role === "super_admin" && <span className="text-[9px] font-mono-data tracking-widest px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 uppercase">Super Admin</span>}
                  {user.role !== "super_admin" && user.role}
                  {user.role === "organizer" && user.organizer_status === "pending" && <span className="text-[9px] font-mono-data tracking-widest px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/5 text-amber-300 uppercase">Pending</span>}
                </div>
                <div className="text-sm text-slate-100">{user.name}</div>
              </div>
              <Button onClick={async () => { await logout(); navigate("/"); }} variant="ghost" size="icon" className="text-slate-400 hover:text-rose-400" data-testid="nav-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </nav>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/auth" data-testid="nav-signin">
              <Button variant="ghost" className="text-slate-300">Sign In</Button>
            </Link>
            <Link to="/auth?mode=register" data-testid="nav-get-started">
              <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Get Started</Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
